import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  ShieldAlert, ArrowLeft, Loader2, RefreshCw, Eye, EyeOff, Trash2, MessageSquareWarning,
  StickyNote, Star, Flag, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDate } from '../../utils/timeFormat';
const STATUS_BADGE = {
  visible:        'bg-emerald-100 text-emerald-800 border-emerald-200',
  pending_review: 'bg-amber-100 text-amber-800 border-amber-200',
  hidden:         'bg-slate-200 text-slate-700 border-slate-300',
  removed:        'bg-rose-100 text-rose-800 border-rose-200',
};

const Stars = ({ value }) => {
  const v = Math.round(Number(value) || 0);
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={12} className={i <= v ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
      ))}
    </span>
  );
};

const RatingsModeration = () => {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionDialog, setActionDialog] = useState(null); // {mode, rating}
  const [reason, setReason] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter === 'all' ? {} : { status: statusFilter };
      const res = await api.get('/admin/ratings/moderation/queue', { params });
      setQueue(res.data?.queue || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load moderation queue');
      setQueue([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const decide = async () => {
    if (!actionDialog) return;
    const { mode, rating } = actionDialog;
    if (['hidden', 'removed'].includes(mode) && !reason.trim()) {
      toast.error('Reason is required to hide or remove a review');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(
        `/admin/ratings/${rating.rating_type}/${rating.id}/moderate`,
        { decision: mode, reason: reason.trim() || null },
      );
      toast.success(`Rating set to ${mode}`);
      setActionDialog(null);
      setReason('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to moderate');
    } finally {
      setSubmitting(false);
    }
  };

  const saveNote = async (rating) => {
    if (!adminNote.trim()) return;
    setSubmitting(true);
    try {
      await api.post(
        `/admin/ratings/${rating.rating_type}/${rating.id}/admin-note`,
        { note: adminNote.trim() },
      );
      toast.success('Admin note saved');
      setAdminNote('');
      setActionDialog(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save note');
    } finally {
      setSubmitting(false);
    }
  };

  const renderRow = (r) => {
    const reviewerLabel = r.rating_type === 'client_rating'
      ? (r.is_anonymous_to_client ? 'Anonymous talent' : (r.reviewer_name || r.reviewer_email))
      : (r.reviewer_name || r.reviewer_email);
    return (
      <Card key={`${r.rating_type}-${r.id}`} className="border-gray-200" data-testid={`mod-row-${r.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500">
                <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                  {r.rating_type === 'client_rating' ? 'Talent → Client' : 'Client → Talent'}
                </Badge>
                {r.is_flagged && (
                  <Badge className="bg-rose-100 text-rose-800 border border-rose-200 text-[10px]">
                    <Flag className="w-3 h-3 mr-1" /> Flagged
                  </Badge>
                )}
                <Badge className={`${STATUS_BADGE[r.moderation_status] || STATUS_BADGE.visible} border text-[10px] capitalize`} data-testid={`mod-status-${r.id}`}>
                  {r.moderation_status.replace('_', ' ')}
                </Badge>
                {r.created_at && (
                  <span>{formatDate(r.created_at)}</span>
                )}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <Stars value={r.overall} />
                <span className="text-sm font-medium">{r.overall}/5</span>
                {r.would_work_again != null && (
                  <span className="text-xs text-gray-600">
                    {r.would_work_again ? '✓ Would work/rehire' : '✗ Would NOT work/rehire'}
                  </span>
                )}
                {r.is_favorite && <Heart aria-label="Favorite"/>}
              </div>
              <p className="mt-2 text-sm text-gray-800">
                <span className="font-medium">{reviewerLabel}</span> rated{' '}
                <span className="font-medium">{r.subject_name || r.subject_email}</span>
                {r.venue_name && <span className="text-gray-500"> · {r.venue_name}</span>}
                {r.shift_date && <span className="text-gray-500"> · {r.shift_date}</span>}
              </p>
            </div>
          </div>

          {/* Comment */}
          {r.comment && (
            <div className="bg-slate-50 border border-slate-200 rounded p-2.5 text-sm" data-testid={`mod-comment-${r.id}`}>
              <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Public feedback</p>
              <p className="text-gray-800 whitespace-pre-line">{r.comment}</p>
            </div>
          )}

          {/* Categories */}
          {r.category_scores && Object.keys(r.category_scores).length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-xs">
              {Object.entries(r.category_scores).map(([k, v]) => (
                <div key={k} className="bg-white border border-gray-100 rounded px-2 py-1 flex items-center justify-between">
                  <span className="text-gray-600 truncate capitalize">{k.replace(/_/g, ' ')}</span>
                  <span className="font-medium text-gray-900">{v}</span>
                </div>
              ))}
            </div>
          )}

          {/* Flag reason banner */}
          {r.flag_reason && (
            <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs flex items-start gap-2">
              <Flag className="w-3.5 h-3.5 text-rose-600 mt-0.5 shrink-0" />
              <span className="text-rose-900"><strong>Flagged:</strong> {r.flag_reason}</span>
            </div>
          )}

          {/* Internal admin note */}
          {r.internal_admin_note && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs flex items-start gap-2" data-testid={`mod-internal-note-${r.id}`}>
              <StickyNote className="w-3.5 h-3.5 text-amber-700 mt-0.5 shrink-0" />
              <span className="text-amber-900"><strong>Internal note:</strong> {r.internal_admin_note}</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-1">
            {r.moderation_status !== 'visible' && (
              <Button size="sm" onClick={() => { setActionDialog({ mode: 'visible', rating: r }); setReason(''); }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`approve-${r.id}`}>
                <Eye className="w-3.5 h-3.5 mr-1" /> Restore visible
              </Button>
            )}
            {r.moderation_status !== 'hidden' && (
              <Button size="sm" variant="outline" onClick={() => { setActionDialog({ mode: 'hidden', rating: r }); setReason(''); }} data-testid={`hide-${r.id}`}>
                <EyeOff className="w-3.5 h-3.5 mr-1" /> Hide
              </Button>
            )}
            {r.moderation_status !== 'removed' && (
              <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => { setActionDialog({ mode: 'removed', rating: r }); setReason(''); }} data-testid={`remove-${r.id}`}>
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => { setActionDialog({ mode: 'note', rating: r }); setAdminNote(r.internal_admin_note || ''); }} data-testid={`note-${r.id}`}>
              <StickyNote className="w-3.5 h-3.5 mr-1" /> Admin note
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-ratings-moderation">
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <div className="flex items-center gap-3 mb-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="-ml-2 text-gray-600" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-6 h-6 text-amber-600" />
              <h1 className="text-2xl font-bold text-gray-900">Ratings Moderation</h1>
              <Badge className="bg-slate-100 text-slate-800 border border-slate-200" data-testid="queue-count">{queue.length}</Badge>
            </div>
            <p className="text-sm text-gray-600 mt-1 max-w-2xl">
              Flagged + pending-review reviews from both directions. Hide or remove abusive,
              retaliatory, or off-topic content. Add internal admin notes that are never shown
              to the public.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="mod-status-filter">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Flagged + pending</SelectItem>
                <SelectItem value="flagged">Flagged only</SelectItem>
                <SelectItem value="pending_review">Pending review</SelectItem>
                <SelectItem value="hidden">Hidden</SelectItem>
                <SelectItem value="removed">Removed</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {loading ? (
          <Card><CardContent className="p-12 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </CardContent></Card>
        ) : queue.length === 0 ? (
          <Card data-testid="mod-empty"><CardContent className="p-12 text-center">
            <MessageSquareWarning className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-base font-semibold text-gray-900">No reviews need attention</p>
            <p className="text-sm text-gray-600 mt-1">The queue is clear.</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-3">{queue.map(renderRow)}</div>
        )}
      </div>

      <Dialog open={!!actionDialog} onOpenChange={(o) => !o && setActionDialog(null)}>
        <DialogContent data-testid="mod-decision-dialog">
          {actionDialog?.mode === 'note' ? (
            <>
              <DialogHeader>
                <DialogTitle>Internal admin note</DialogTitle>
                <DialogDescription>
                  This note is visible only to admins. The public, talent, and clients never see it.
                </DialogDescription>
              </DialogHeader>
              <Textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} rows={4} data-testid="mod-admin-note-input" placeholder="Context for future moderation decisions…" />
              <DialogFooter>
                <Button variant="outline" onClick={() => setActionDialog(null)} disabled={submitting}>Cancel</Button>
                <Button onClick={() => saveNote(actionDialog.rating)} disabled={submitting} data-testid="mod-save-note-btn">
                  {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save note
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>
                  {actionDialog?.mode === 'visible' && 'Restore review to public'}
                  {actionDialog?.mode === 'hidden'  && 'Hide review from public'}
                  {actionDialog?.mode === 'removed' && 'Remove review permanently'}
                </DialogTitle>
                <DialogDescription>
                  {actionDialog?.mode === 'visible'
                    ? 'The review will be visible again to its intended audience.'
                    : 'Add a moderation reason — required for the audit log.'}
                </DialogDescription>
              </DialogHeader>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason for moderation action…" data-testid="mod-reason-input" />
              <DialogFooter>
                <Button variant="outline" onClick={() => setActionDialog(null)} disabled={submitting}>Cancel</Button>
                <Button
                  onClick={decide}
                  disabled={submitting || (actionDialog?.mode !== 'visible' && !reason.trim())}
                  className={actionDialog?.mode === 'visible' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}
                  data-testid="mod-decide-btn"
                >
                  {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                  Confirm
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Heart = ({ ...props }) => (
  <span {...props} className="inline-flex items-center text-rose-500" title="Favorite">❤</span>
);

export default RatingsModeration;
